'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import api from '@/lib/api'
import { useI18n } from '@/components/I18nProvider'

type RequestInput = { email: string }
type VerifyInput = {
  email: string
  recovery_code: string
  new_password: string
  confirm_password: string
}

// Classify the verify-stage error so we can render the right recovery action.
type VerifyErrorKind = 'expired' | 'locked' | 'invalid' | 'server'

function classifyVerifyError(err: unknown): { kind: VerifyErrorKind; message: string } {
  const detail = isAxiosError<{ detail?: string }>(err)
    ? String(err.response?.data?.detail || '')
    : err instanceof Error
      ? err.message
      : ''
  const status = isAxiosError(err) ? err.response?.status : undefined
  const lower = detail.toLowerCase()

  if (lower.includes('expired')) {
    return { kind: 'expired', message: detail }
  }
  if (status === 429 || lower.includes('too many') || lower.includes('locked')) {
    return { kind: 'locked', message: detail }
  }
  if (status && status >= 500) {
    return { kind: 'server', message: detail || 'Server error. Please try again.' }
  }
  return { kind: 'invalid', message: detail || 'Invalid code' }
}

export function ForgotPasswordForm() {
  const { t } = useI18n()
  const router = useRouter()
  const [stage, setStage] = useState<'request' | 'verify'>('request')
  const [emailLocked, setEmailLocked] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [info, setInfo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [verifyErrorKind, setVerifyErrorKind] = useState<VerifyErrorKind | null>(null)

  const requestSchema = useMemo(
    () => z.object({ email: z.string().email(t({ es: 'Email invalido', en: 'Invalid email' })) }),
    [t]
  )

  const verifySchema = useMemo(
    () =>
      z
        .object({
          email: z.string().email(t({ es: 'Email invalido', en: 'Invalid email' })),
          recovery_code: z.string().min(6, t({ es: 'Codigo de 6 digitos', en: '6-digit code' })),
          new_password: z.string().min(8, t({ es: 'Minimo 8 caracteres', en: 'At least 8 characters' })),
          confirm_password: z.string().min(8, t({ es: 'Minimo 8 caracteres', en: 'At least 8 characters' })),
        })
        .refine((values) => values.new_password === values.confirm_password, {
          message: t({ es: 'Las contrasenas no coinciden', en: "Passwords don't match" }),
          path: ['confirm_password'],
        }),
    [t]
  )

  const requestForm = useForm<RequestInput>({ resolver: zodResolver(requestSchema) })
  const verifyForm = useForm<VerifyInput>({
    resolver: zodResolver(verifySchema),
    defaultValues: { email: emailLocked || '' },
  })

  // Return to Stage 1 and pre-fill the email the user originally entered.
  const returnToRequest = () => {
    setStage('request')
    setError(null)
    setInfo(null)
    setVerifyErrorKind(null)
    verifyForm.reset()
    if (emailLocked) {
      requestForm.setValue('email', emailLocked)
    }
  }

  const onRequest = async (data: RequestInput) => {
    setIsLoading(true)
    setError(null)
    setInfo(null)
    try {
      const response = await api.post('/auth/password-recovery/request', { email: data.email })
      setEmailLocked(data.email)
      verifyForm.setValue('email', data.email)
      setStage('verify')
      setVerifyErrorKind(null)
      setInfo(response.data?.message || t({ es: 'Revisa tu email', en: 'Check your email' }))
      setIsLoading(false)
    } catch (err) {
      const message = isAxiosError<{ detail?: string }>(err)
        ? err.response?.data?.detail || t({ es: 'No se pudo enviar el codigo', en: "Couldn't send code" })
        : err instanceof Error
          ? err.message
          : t({ es: 'No se pudo enviar el codigo', en: "Couldn't send code" })
      setError(message)
      setIsLoading(false)
    }
  }

  const onVerify = async (data: VerifyInput) => {
    setIsLoading(true)
    setError(null)
    setInfo(null)
    setVerifyErrorKind(null)
    try {
      await api.post('/auth/password-recovery/verify', {
        email: data.email,
        recovery_code: data.recovery_code,
        new_password: data.new_password,
      })
      setInfo(
        t({
          es: 'Contrasena actualizada. Ya puedes iniciar sesion.',
          en: 'Password updated. You can sign in now.',
        })
      )
      setIsLoading(false)
      setTimeout(() => {
        router.push('/login')
      }, 2000)
    } catch (err) {
      const { kind, message } = classifyVerifyError(err)
      setError(message)
      setVerifyErrorKind(kind)
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-3">
      {info ? <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{info}</div> : null}
      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      {stage === 'request' ? (
        <form onSubmit={requestForm.handleSubmit(onRequest)} className="space-y-3">
          <Input
            label={t({ es: 'Email', en: 'Email' })}
            type="email"
            placeholder={t({ es: 'tu@email.com', en: 'you@email.com' })}
            error={requestForm.formState.errors.email?.message}
            className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
            {...requestForm.register('email')}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full rounded-2xl bg-[linear-gradient(135deg,#0f766e,#14b8a6_58%,#2dd4bf)] py-3 text-[15px] shadow-[0_22px_50px_-28px_rgba(13,148,136,0.56)] hover:translate-y-0"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t({ es: 'Enviar', en: 'Send' })}
          </Button>
        </form>
      ) : (
        <form onSubmit={verifyForm.handleSubmit(onVerify)} className="space-y-3">
          <Input
            label={t({ es: 'Email', en: 'Email' })}
            type="email"
            disabled={!!emailLocked}
            error={verifyForm.formState.errors.email?.message}
            className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
            {...verifyForm.register('email')}
          />
          <Input
            label={t({ es: 'Codigo', en: 'Code' })}
            placeholder="123456"
            error={verifyForm.formState.errors.recovery_code?.message}
            className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
            {...verifyForm.register('recovery_code')}
          />
          <Input
            label={t({ es: 'Nueva contrasena', en: 'New password' })}
            type="password"
            placeholder="********"
            error={verifyForm.formState.errors.new_password?.message}
            className="rounded-2xl border-black/0 bg-[#f2f7f7] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:bg-white focus:ring-[#14b8a6]/10"
            {...verifyForm.register('new_password')}
          />
          <Input
            label={t({ es: 'Confirmar contrasena', en: 'Confirm password' })}
            type="password"
            placeholder="********"
            error={verifyForm.formState.errors.confirm_password?.message}
            className="rounded-2xl border-black/0 bg-[#f2f7f7] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:bg-white focus:ring-[#14b8a6]/10"
            {...verifyForm.register('confirm_password')}
          />

          {/* Recovery actions — shown whenever there is an error in Stage 2 */}
          {verifyErrorKind !== null ? (
            <div className="space-y-2">
              {/* Expired or locked: primary CTA is to request a new code */}
              {(verifyErrorKind === 'expired' || verifyErrorKind === 'locked') ? (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full rounded-2xl bg-[linear-gradient(135deg,#0f766e,#14b8a6_58%,#2dd4bf)] py-3 text-[15px] shadow-[0_22px_50px_-28px_rgba(13,148,136,0.56)] hover:translate-y-0"
                  disabled={isLoading}
                  onClick={returnToRequest}
                >
                  {t({ es: 'Solicitar nuevo codigo', en: 'Request new code' })}
                </Button>
              ) : null}

              {/* Invalid / server: allow retry (form stays) or go back to correct email */}
              {(verifyErrorKind === 'invalid' || verifyErrorKind === 'server') ? (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full rounded-2xl"
                  disabled={isLoading}
                  onClick={returnToRequest}
                >
                  {t({ es: 'Volver e intentar con otro email', en: 'Go back and try a different email' })}
                </Button>
              ) : (
                /* For expired/locked, also offer going back as a secondary option */
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full rounded-2xl"
                  disabled={isLoading}
                  onClick={returnToRequest}
                >
                  {t({ es: 'Volver', en: 'Go back' })}
                </Button>
              )}
            </div>
          ) : (
            /* No error yet — show normal submit button */
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full rounded-2xl bg-[linear-gradient(135deg,#0f766e,#14b8a6_58%,#2dd4bf)] py-3 text-[15px] shadow-[0_22px_50px_-28px_rgba(13,148,136,0.56)] hover:translate-y-0"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {t({ es: 'Actualizar', en: 'Update' })}
            </Button>
          )}
        </form>
      )}
    </div>
  )
}
